import { ApolloErrorCode } from 'connect-graphql/errors';
import {
  cacheExchange,
  Client,
  type ClientOptions,
  type CombinedError,
  type Exchange,
  fetchExchange,
  mapExchange,
} from 'urql';
import { type Optional } from 'utility-types';

import { Auth0Config } from '@/src/integrations/auth0';

/**
 * Defines whether or not the {@link AuthenticationRedirectExchange} is currently in the process of
 * handling an unauthenticated error and forcing a reload.  This guards against multiple
 * simultaneous GraphQL operations all failing with unauthenticated errors and all triggering the
 * reload logic at the same time.
 */
let IsHandlingAuthenticationError = false;

/**
 * The {@link Window.sessionStorage} key under which the timestamp of the most recent
 * authentication-recovery reload is recorded.
 *
 * A recent timestamp on a subsequent unauthenticated error indicates that the recovery reload did
 * not resolve the unauthenticated state, which is used to escape the reload loop.
 */
const AuthRecoveryReloadKey = 'craft:auth-recovery-reload-at';

/**
 * The window, in milliseconds, within which a repeated unauthenticated error after a recovery
 * reload is treated as a redirect loop rather than a newly expired session.
 */
const AuthRecoveryWindowMs = 10_000;

/**
 * Determines whether or not the {@link CombinedError} carries the
 * {@link ApolloErrorCode.NOT_AUTHENTICATED} code, which the GraphQL API proxy /api/graphql and
 * the connect-graphql context emit only when the request has no authenticated user.
 */
export const isNotAuthenticatedError = (error: CombinedError): boolean =>
  error.graphQLErrors.some(
    graphQlError => graphQlError.extensions.code === ApolloErrorCode.NOT_AUTHENTICATED,
  );

/**
 * Returns whether or not the most recent detected {@link GraphQlError} with code
 * {@link ApolloErrorCode.NOT_AUTHENTICATED} that triggered a reload in order to recover from an
 * unauthenticated state occurred within the {@link AuthRecoveryWindowMs} window, which is used to
 * determine whether or not to trigger another reload or to route directly to the logout path in
 * order to break out of a potential reload loop.
 *
 * @returns {boolean}
 *   Whether or not the last recovery reload failed to recover.
 */
const didLastReloadFailToRecover = (): boolean => {
  try {
    const raw = window.sessionStorage.getItem(AuthRecoveryReloadKey);
    if (raw === null) {
      return false;
    }
    const numeric = Number.parseInt(raw, 10);
    if (Number.isFinite(numeric)) {
      return Date.now() - numeric < AuthRecoveryWindowMs;
    }

    return false;
  } catch {
    return false;
  }
};

/**
 * Records the current time as the most recent authentication-recovery reload in
 * {@link Window.sessionStorage}, confirming the write persisted via a read-after-write.
 *
 * The confirmation matters because the recorded timestamp is the only signal
 * {@link didLastReloadFailToRecover} has to escalate a looping reload to a logout. If the write is
 * silently dropped (blocked storage, a privacy mode that no-ops {@link Storage.setItem}, quota
 * exhaustion), a plain reload could loop indefinitely, so the caller falls back to logout when this
 * returns {@link false}.
 *
 * @returns {boolean}
 *   Whether the reload timestamp was written and read back successfully.
 */
const recordAuthRecoveryReload = (): boolean => {
  try {
    const value = String(Date.now());
    window.sessionStorage.setItem(AuthRecoveryReloadKey, value);
    return window.sessionStorage.getItem(AuthRecoveryReloadKey) === value;
  } catch {
    return false;
  }
};

/**
 * Clears the recorded authentication-recovery reload timestamp from {@link Window.sessionStorage}.
 */
const clearAuthRecoveryReload = (): void => {
  try {
    window.sessionStorage.removeItem(AuthRecoveryReloadKey);
  } catch {
    // See recordAuthRecoveryReload: a storage failure here is safe to ignore.
  }
};

/**
 * Forces the user back to the login flow when a urql operation fails because the request is no
 * longer authenticated, e.g. the session expired while a tab was left open.
 *
 * On the first failure the current URL is reloaded, handing control to the NextJS middleware, which
 * redirects an unauthenticated request to `welcome` with a `returnTo` pointing back at the current
 * page.
 *
 * However, if a recovery reload was already attempted within {@link AuthRecoveryWindowMs} and the
 * request is still unauthenticated, a plain reload cannot resolve the state - the NextJS session is
 * valid but the GraphQL layer rejects the user - so the session is cleared via the Auth0 logout
 * path, breaking the reload loop and routing the next request to login.
 *
 * The same logout path is taken when the reload timestamp cannot be persisted, since without it a
 * subsequent failure is indistinguishable from a first failure and the reload would loop forever.
 */
export const recoverFromNotAuthenticatedError = (): void => {
  if (IsHandlingAuthenticationError || typeof window === 'undefined') {
    return;
  }
  IsHandlingAuthenticationError = true;

  const failedToRecover = didLastReloadFailToRecover();
  const recoveryReloadPersisted = failedToRecover ? false : recordAuthRecoveryReload();
  if (failedToRecover || !recoveryReloadPersisted) {
    clearAuthRecoveryReload();
    window.location.assign(Auth0Config.fromEnvironment().logoutPath);
    return;
  }
  window.location.assign(window.location.href);
};

/**
 * An exchange that forces a full page reload whenever any operation fails due to a
 * {@link GraphQlError} with code {@link ApolloErrorCode.NOT_AUTHENTICATED}, which indicates that
 * the user's session is no longer valid and they need to be logged back in.
 *
 * Each authenticated page is protected by a combination of Auth0 session lookup and a lookup for
 * the corresponding {@link AuthenticatedUserWithSelectedRole} in the database before allowing
 * access, redirecting back out if either fails.  However, it is possible that after a page load,
 * their session expires or their account is otherwise changed such that either the session is
 * still valid but has expired or their session is no longer valid and they need to be logged out.
 *
 * To accomplish this, the Urql client intercepts these errors and forces a full page reload, which
 * routes back through the NextJS middleware that performs the session check and redirects to the
 * login page if the session is no longer valid or allows the page load to proceed if the session
 * is still valid but the GraphQL layer was out of sync with the session state for some reason.
 *
 * @see {recoverFromNotAuthenticatedError}
 */
const AuthenticationRedirectExchange: Exchange = mapExchange({
  onError(error) {
    if (isNotAuthenticatedError(error)) {
      recoverFromNotAuthenticatedError();
    }
  },
});

const StandardExchanges: Exchange[] = [
  cacheExchange,
  AuthenticationRedirectExchange,
  fetchExchange,
];
const LtiExchanges: Exchange[] = [cacheExchange, fetchExchange];

interface UrqlGraphQlClientOptions extends Optional<Omit<ClientOptions, 'exchanges'>, 'url'> {}

export class UrqlGraphqlClient extends Client {
  constructor(
    { url = '/api/graphql', ...options }: UrqlGraphQlClientOptions,
    exchanges: Exchange[] = StandardExchanges,
  ) {
    super({ ...options, exchanges, url });
  }
}

interface UrqlLtiClientOptions extends Omit<UrqlGraphQlClientOptions, 'fetchOptions' | 'url'> {
  readonly ltiUserData: string;
}

export class UrqlLtiClient extends UrqlGraphqlClient {
  constructor({ ltiUserData, ...options }: UrqlLtiClientOptions) {
    super(
      {
        ...options,
        fetchOptions: () => ({
          headers: {
            'x-ltiuser-data': ltiUserData,
          },
        }),
        url: '/api/graphql-lti',
      },
      LtiExchanges,
    );
  }
}
